import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

// Composants
import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';
import { AvatarComponent } from 'src/app/@shared/components/user/avatar/avatar.component';

// Modèles
import { Conversation } from 'src/app/@shared/models/messaging/conversation.model';
import { Person } from 'src/app/@shared/models/person/abstract/person.model';
import { ConversationDetailsItemComponent } from "../conversation-details-item/conversation-details-item.component";
import { ConversationItemFormComponent } from "../conversation-item-form/conversation-item-form.component";

@Component({
	selector: 'app-conversation-details',
	imports: [
		AvatarModule,
		AvatarGroupModule,
		AvatarComponent,
		ButtonModule,
		ProgressSpinnerModule,
		ConversationDetailsItemComponent,
		ConversationItemFormComponent,
		DividerModule,
		TooltipModule,
	],
	templateUrl: './conversation-details.component.html',
	styleUrl: './conversation-details.component.scss'
})
export class ConversationDetailsComponent extends BaseComponent {

	/*----------------------------------------------------------------------------------------
	 * Gestion de la vue
	 ----------------------------------------------------------------------------------------*/

	@ViewChild('messageContainer', { static: false }) private _messageContainer!: ElementRef;

	@Output() public conversationChanged: EventEmitter<Conversation> = new EventEmitter<Conversation>();
	@Output() public conversationCreated: EventEmitter<Conversation> = new EventEmitter<Conversation>();

	public firstUnreadItemIndex: number = -1;
	public hasVisibleMessages: boolean = false;
	public participantsOverflow: number = 0;
	public participantsToShow: Person[] = [];
	private _followStateRefreshed: boolean = false;
	private _scrollNeeded: boolean = false;

	/*----------------------------------------------------------------------------------------
	 * Données
	 ----------------------------------------------------------------------------------------*/

	private _conversation: Conversation | null = null;
	public get conversation(): Conversation | null {
		return this._conversation;
	}
	@Input()
	public set conversation(value: Conversation | null) {
		if (value) {
			// On ne (re)scrolle et ne réinitialise l'état de suivi que sur un réel changement de
			// conversation : le panel réinjecte la même conversation après follow/unfollow ou refresh,
			// et on ne veut alors ni re-scroller la liste ni rouvrir la fenêtre d'auto-follow.
			const isNewConversation = this._conversation?.id !== value.id;
			this._conversation = value;
			this._computeParticipants();
			this._computeHasVisibleMessages();
			if (isNewConversation) {
				this._followStateRefreshed = false;
				this._findFirstUnreadItem();
				this._scrollNeeded = true;
			}
		} else {
			this._conversation = null;
			this._computeParticipants();
		}
	}

	/*----------------------------------------------------------------------------------------
	 * Accesseurs
	 ----------------------------------------------------------------------------------------*/



	/*----------------------------------------------------------------------------------------
	 * Méthodes publiques
	 ----------------------------------------------------------------------------------------*/

	ngAfterViewChecked(): void {
		// Effectuer le défilement si nécessaire après chaque vérification de la vue
		if (this._scrollNeeded) {
			this._scrollNeeded = this._scrollToBottom();
		}
	}

	public getParticipantLabel(participant: Person): string {
		if (participant.user?.avatarLabel) {
			return participant.user.avatarLabel;
		}
		const first = participant.firstName?.charAt(0) || '';
		const last = participant.lastName?.charAt(0) || '';
		return (first + last).toUpperCase() || '?';
	}

	public onConversationCreated(conversation: Conversation): void {
		this.conversationCreated.emit(conversation);
	}

	public onItemSaved(): void {
		this._scrollNeeded = true;
		this._computeHasVisibleMessages();
		this._refreshFollowStateIfNeeded();
	}

	public onToggleFollow(): void {
		this._toggleFollow();
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes protégées
	 ----------------------------------------------------------------------------------------*/



	/*----------------------------------------------------------------------------------------
	 * Méthodes privées
	 ----------------------------------------------------------------------------------------*/

	private _computeHasVisibleMessages(): void {
		// Un message « visible » = un item non-brouillon. Mémoïsé (recalculé au changement
		// de conversation et après envoi) pour éviter un getter itératif lié au template.
		this.hasVisibleMessages = !!this._conversation?.items?.some(item => !item.hasStatus(this.Status.Draft));
	}

	private _computeParticipants(): void {
		// Participants visibles uniquement en conversation privée (internes + externes)
		const all: Person[] = this._conversation && this._conversation.private
			? [...(this._conversation.internalParticipants || []), ...(this._conversation.externalParticipants || [])]
			: [];
		const max = 5;
		this.participantsToShow = all.slice(0, max);
		this.participantsOverflow = Math.max(0, all.length - max);
	}

	private _findFirstUnreadItem(): void {
		if (this._conversation) {
			this.firstUnreadItemIndex = this._conversation.items.findIndex(item => !item.hasStatus(this.Status.Read));
		}
	}

	private _refreshFollowStateIfNeeded(): void {
		// Auto-follow backend : au premier message d'un ClientContact dans une conv publique,
		// le statut FOLLOWED est posé côté serveur. On resynchronise une seule fois par conversation
		// chargée, et uniquement si on ne suit pas déjà la conv (et qu'elle est publique).
		const conversation = this._conversation;
		if (!conversation || conversation.private || this._followStateRefreshed || conversation.hasStatus(this.Status.Followed)) {
			return;
		}
		this._followStateRefreshed = true;
		this.conversationService.get$(conversation.id).subscribe((updated: Conversation) => {
			this._conversation = updated;
			this.conversationChanged.emit(updated);
		});
	}

	private _toggleFollow(): void {
		const conversation = this._conversation;
		if (!conversation) {
			return;
		}
		const isFollowed = conversation.hasStatus(this.Status.Followed);
		const request$ = isFollowed
			? this.conversationService.unfollow$(conversation)
			: this.conversationService.follow$(conversation);
		request$.subscribe((updated: Conversation) => {
			this._conversation = updated;
			this.conversationChanged.emit(updated);
		});
	}

	private _scrollToBottom(): boolean {
		try {
			if (this._messageContainer) {
				// Définir scrollTop à scrollHeight pour défiler tout en bas
				this._messageContainer.nativeElement.scrollTop = this._messageContainer.nativeElement.scrollHeight;
				return false; // Pas besoin de défiler à nouveau
			}
		} catch (err) {
			console.error('Erreur lors du défilement vers le bas:', err);
		}
		return true; // Besoin de défiler à nouveau
	}

}
